import { call, put, takeLatest } from "redux-saga/effects";
import { types } from "reducers/discounts";
import * as discountMiddlewares from "middleware/discounts";
import * as form from "middleware/form";
import { actions as notificationActions } from "reducers/notification";
import { download, downloadPdf } from "util/download";
import * as i18n from "util/i18n";
import { selectors as sessionSelectors } from "reducers/session";
import { store } from "store";
import { credentialsErrorMessage } from "util/validateMessage";
import { handleError } from "sagas";
import { push } from "connected-react-router";

const sagas = [
    takeLatest(types.LOAD_LIST_REQUEST, loadListDiscountRequest),
    takeLatest(types.LOAD_TRANSACTION_REQUEST, loadTransactionRequest),
    // takeLatest(types.LOAD_MORE_LIST_REQUEST, loadMoreListThirdRequest),
    takeLatest(types.DOWNLOAD_FILE_ECHEQ_DETAIL_REQUEST, downloadFileEcheqDetailRequest),
    takeLatest(types.DOWNLOAD_FILE_ECHEQ_BATCH_REQUEST, downloadFileEcheqBatchRequest),
    takeLatest(types.DOWNLOAD_DOCUMENT_REQUEST, downloadDocumentRequest),

    takeLatest(types.LOAD_LIST_ECHEQ_REQUEST, loadListEcheqRequest),
    takeLatest(types.LOAD_DISCOUNT_SIMULATION_REQUEST, simulateDiscountRequest),

    takeLatest(types.DISCOUNT_REQUIRED_CREDENTIALS_REQUEST, listCredentialsGroups),
    takeLatest(types.NEW_DISCOUNT_TICKET_REQUEST, openLotRequest),

    takeLatest(types.LOAD_LIST_ECHEQ_LOT_REQUEST, loadListEcheqLotRequest),

    takeLatest(types.GET_PARTIAL_ECHEQS_REQUEST, getPartialEcheqs),
    takeLatest(types.REFRESH_SESSION, refreshSession),
    takeLatest(types.SEND_TRANSACTION_TICKET_REQUEST, sendTransactionTicket),
];

export default sagas;

function* loadListDiscountRequest() {
    const customFilters = { ...store?.getState()?.discounts?.discountFilters };
    const discountFilters = {
        dateFrom: null,
        dateTo: null,
        idBatch: "",
        status: "",
        pageNumber: 1,
        ...customFilters,
    };

    const response = yield call(discountMiddlewares.loadListRequest, discountFilters);
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["discountPage"]));
        yield put({ type: types.LOAD_LIST_FAILURE });
    } else {
        yield put({
            type: types.LOAD_LIST_SUCCESS,
            transactionsEcheqDiscount: response.data.data.transactionsEcheqDiscount,
        });
    }
}

function* loadListEcheqLotRequest({ idTransactionLot }) {
    const response = yield call(discountMiddlewares.loadListEcheqLotRequest, {
        idTransactionLot: idTransactionLot.id,
    });
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["discountPage"]));
        yield put({ type: types.LOAD_LIST_ECHEQ_LOT_FAILURE });
    } else {
        yield put({
            type: types.LOAD_LIST_ECHEQ_LOT_SUCCESS,
            echeqDocuments: response.data.data.echeqDocuments,
        });
    }
}

function* loadTransactionRequest({ idTransaction }) {
    const response = yield call(form.readTransaction, idTransaction);
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["form"]));
        yield put({ type: types.LOAD_TRANSACTION_FAILURE });
    } else {
        const { data, idTransactionStatus, idActivity } = response.data.data.transaction;
        yield put({
            type: types.LOAD_TRANSACTION_SUCCESS,
            data: { ...data, idTransactionStatus, idActivity },
        });
    }
}

function* downloadFileEcheqDetailRequest({ idTransactionLot, echeqNumber, idBatch }) {
    const { data, type } = yield call(
        discountMiddlewares.downloadDetailEcheqDiscountMiddleware,
        idTransactionLot,
        echeqNumber,
        idBatch,
    );

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_FILE_ECHEQ_DETAIL_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["form"]));
    } else {
        const { content, fileName } = data.data;

        downloadPdf(fileName, content);

        yield put({ type: types.DOWNLOAD_FILE_ECHEQ_DETAIL_SUCCESS });
    }
}

function* downloadFileEcheqBatchRequest({ discountDetail }) {
    const params = { ...discountDetail, status: "" };

    const { data, type } = yield call(discountMiddlewares.downloadDetailEcheqBatchMiddleware, params);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_FILE_ECHEQ_BATCH_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["form"]));
    } else {
        const { content, fileName } = data.data;

        downloadPdf(fileName, content);

        yield put({ type: types.DOWNLOAD_FILE_ECHEQ_BATCH_SUCCESS });
    }
}

function* downloadDocumentRequest({ fileId }) {
    const { data, type } = yield call(discountMiddlewares.downloadDocumentMiddleware, fileId);
    if (type === "W") {
        yield put({ type: types.DOWNLOAD_DOCUMENT_FAILURE });
    } else {
        const { content, fileName } = data.data;
        download(fileName, content);
        yield put({ type: types.DOWNLOAD_DOCUMENT_SUCCESS });
    }
}

function* loadListEcheqRequest({ filterEmpty }) {
    const customFilters = { ...store?.getState()?.discounts?.echeqFilters };

    const echeqFilters = {
        extraInfo: "",
        payedFrom: null,
        payedTo: null,
        createdFrom: null,
        createdTo: null,
        emitedCheck: "",
        payedCheck: "",
        status: "TODOS",
        grouper: "",
        number: "",
        id: "",
        emitted: "",
        receiver: "",
        option: "",
        character: "",
        ownership: "TODOS",
        orderColumName: "",
        orderDirection: "",
        reportInfo: "",
        dateFrom: null,
        dateTo: null,
        amountFrom: null,
        amountTo: null,
        pageNumber: 1,
        ...(filterEmpty ? {} : customFilters),
    };

    const formattedStatus = echeqFilters.status === "TODOS" ? "" : echeqFilters.status;

    const response = yield call(discountMiddlewares.loadListEcheqRequest, {
        ...echeqFilters,
        status: formattedStatus,
    });
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["discountsPage"]));
        yield put({ type: types.LOAD_LIST_ECHEQ_FAILURE });
    } else {
        const {
            pageNumber,
            totalPages,
            echeqs,
            hasPartials: firstHasPartials,
            ...listEcheqResponse
        } = response.data.data;

        let listEcheq = [];
        if (pageNumber === 1 || !echeqs.length) {
            listEcheq = echeqs;
        } else {
            const reduxEcheqs = store?.getState()?.discounts?.listEcheq;
            listEcheq = [...reduxEcheqs, ...echeqs];
        }

        yield put({
            type: types.LOAD_LIST_ECHEQ_SUCCESS,
            listEcheqResponse,
            listEcheq,
            hasPartials: firstHasPartials,
            pageNumber,
            totalPages,
        });
    }
}

function* simulateDiscountRequest({ newDiscount }) {
    const { userId } = sessionSelectors.getUser(store.getState());
    const response = yield call(discountMiddlewares.loadDiscountSimulationRequest, { ...newDiscount, userId });

    if (response.type === "W") {
        if (response.data.code === "API554W") {
            yield put({ type: types.LOAD_DISCOUNT_SIMULATION_FAILURE });
            const message = credentialsErrorMessage(response.data?.data) || i18n.get("global.unexpectedError");
            yield put(notificationActions.showNotification(message, "error", ["discountsPage"]));
        } else {
            yield put(notificationActions.showNotification(response.data.data.message, "warning", ["discountsPage"]));
            yield put({ type: types.LOAD_DISCOUNT_SIMULATION_FAILURE });
        }
    } else {
        yield put({
            type: types.LOAD_DISCOUNT_SIMULATION_SUCCESS,
            discountSimulation: response.data.data,
            idTransactionSimulate: response.data.idTransaction,
        });
    }
}

function* listCredentialsGroups({ params }) {
    const { userId: idUser } = sessionSelectors.getUser(store.getState());
    const { isOwnEcheq } = params;

    let idActivityToRead;
    if (isOwnEcheq === true) {
        idActivityToRead = "openLot.ownEcheq.send";
    } else {
        idActivityToRead = "load.echeq.discounts";
    }
    const request = { idUser, active: true, idActivityToRead };
    const response = yield call(discountMiddlewares.listCredentialsGroups, request);

    if (response && response.status === 200) {
        yield put({
            type: types.DISCOUNT_REQUIRED_CREDENTIALS_SUCCESS,
            discountRequiredCredentials: response.data.data?.groups[0]?.credentials,
        });
    } else {
        yield put({
            type: types.DISCOUNT_REQUIRED_CREDENTIALS_FAILURE,
        });
    }
}

function* openLotRequest({ params, formikBag, credential }) {
    console.log("params shaka", params);

    const { userId } = sessionSelectors.getUser(store.getState());
    const { idTransactionSimulate, discountSimulation, newDiscount } = store.getState().discounts;

    const { isOwnEcheq, account, idActivity } = params || newDiscount;
    const idAccount = account?.idProduct;

    const lastParams = {
        ...params,
        idTransactionSimulate: params.idTransactionSimulate || idTransactionSimulate || params.idTransactionBatch,
        userId,
        echeqs: discountSimulation?.echeqs ?? [],
        isOwnEcheq,
        idAccount,
    };

    let response;

    const ticketStates = {
        COR020W: "INVALID_CREDENTIAL",
        COR060I: "PROCESSING",
        COR023I: "PENDING",
        COR000I: "FINISHED",
        API516W: "FAILED",
        FAILED: "FAILED",
    };
    try {
        if (idActivity === "openLot.ownEcheq.send") {
            response = yield call(discountMiddlewares.openLotOwnEcheq, lastParams);
        } else {
            response = yield call(discountMiddlewares.openLotOtherEcheq, lastParams);
        }

        console.log("response ", response);

        if (response && response.status === 200) {
            const { code, message } = response.data;

            if (code && response.type === "W") {
                const alertMessage =
                    (response.data.data?.otp && i18n.get("validate.secondFactor.invalid")) ||
                    credentialsErrorMessage(response.data?.data) ||
                    message;

                yield put(
                    notificationActions.showNotification(alertMessage, "error", [
                        "DiscountReviewPage",
                        "discounts", // scope de comprobantes
                    ]),
                );

                if (response.data?.data?.otp || response.data?.data?.password) {
                    formikBag.setErrors({ credential: alertMessage });
                }
                return;
            }

            yield put({
                type: types.NEW_DISCOUNT_TICKET_SUCCESS,
                discountTicket: {
                    status: ticketStates[code],
                    ...response.data,
                },
            });
        } else {
            yield put(
                notificationActions.showNotification(response.data.message, "error", [
                    "DiscountReviewPage",
                    "discounts", // scope de comprobantes
                ]),
            );
            yield put({
                type: types.NEW_DISCOUNT_TICKET_FAILURE,
                discountTicket: {
                    status: ticketStates.FAILED,
                    ...response.data,
                },
            });
        }
    } catch (error) {
        console.log("error ", error);
        yield put({
            type: types.NEW_DISCOUNT_TICKET_FAILURE,
            discountTicket: {
                status: ticketStates.FAILED,
                ...error?.data,
            },
        });

        // const _error = {
        //     ...error,
        //     data: {
        //         ...error.data,
        //         message: error?.data?.data?.message || error?.data?.message,
        //     },
        // };
        // yield call(handleError, _error);
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* getPartialEcheqs({ idTransactionBatch }) {
    // const { echeqDocuments } = store.getState().discounts;

    const response = yield call(discountMiddlewares.getPartialEcheqs, { idTransactionBatch });

    if (response && response.status === 200) {
        yield put({
            type: types.GET_PARTIAL_ECHEQS_SUCCESS,
            negotiatePartial: response.data.data,
        });
    } else {
        yield put({
            type: types.GET_PARTIAL_ECHEQS_FAILURE,
        });
    }
}

function* refreshSession() {
    try {
        yield call(discountMiddlewares.refreshSession, {});
    } catch (error) {
        // se maneja error
    }
}

function* sendTransactionTicket({ request }) {
    const { toAddress } = request;
    const { body } = request;
    const transaction_id = request.transactionId;
    const response = yield call(discountMiddlewares.sendTransactionTicket, { transaction_id, toAddress, body });

    if (response && response.status === 200) {
        yield put({
            type: types.SEND_TRANSACTION_TICKET_SUCCESS,
        });
        yield put(
            notificationActions.showNotification(i18n.get("discount.ticket.send.success"), "success", [
                "discountsPage",
            ]),
        );
    } else {
        yield put({
            type: types.SEND_TRANSACTION_TICKET_FAILURE,
        });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["discountsPage"]));
    }
}
